import { z } from '../zod.js';
import { DashboardUrlField } from '../shared/dashboardUrl.js';
import { PaginatedListSchema } from '../shared/paginatedList.js';
import {
  WebhookEventValidationEnum,
  WebhookStatusValidationEnum,
  WebhookVersionValidationEnum,
} from './enum.js';

export const WebhookResponseValidationSchema = z
  .object({
    id: z.string().meta({ description: 'Unique identifier for the webhook.' }),
    name: z.string().meta({ description: 'Internal label for the endpoint.' }),
    url: z.string().meta({ description: 'The https URL deliveries are POSTed to.' }),
    events: z
      .array(WebhookEventValidationEnum)
      .meta({ description: 'Events this endpoint receives.' }),
    status: WebhookStatusValidationEnum,
    apiVersion: WebhookVersionValidationEnum,
    customHeaders: z.record(z.string(), z.string()).meta({
      description: 'Extra headers sent with every delivery. An empty object when none are set.',
    }),
    deliveryCount: z
      .number()
      .int()
      .meta({ description: 'Total delivery attempts made to this endpoint.' }),
    successCount: z
      .number()
      .int()
      .meta({ description: 'Deliveries that received a 2xx response.' }),
    failureCount: z
      .number()
      .int()
      .meta({ description: 'Deliveries that failed after all retries.' }),
    lastTriggeredAt: z.string().nullable().meta({
      description: 'ISO-8601 time of the most recent delivery attempt, or null if never triggered.',
    }),
    lastSuccessAt: z.string().nullable().meta({
      description: 'ISO-8601 time of the most recent successful delivery, or null.',
    }),
    lastFailureAt: z.string().nullable().meta({
      description: 'ISO-8601 time of the most recent failed delivery, or null.',
    }),
    createdAt: z.string().meta({ description: 'ISO-8601 time the webhook was created.' }),
    updatedAt: z.string().meta({ description: 'ISO-8601 time the webhook was last changed.' }),
  })
  .meta({ description: 'A webhook endpoint subscribed to one or more events.' });

export const WebhookWithSecretResponseValidationSchema = WebhookResponseValidationSchema.extend({
  secret: z.string().meta({
    description:
      'Signing secret for this endpoint. The API returns it only in this response; it can be viewed or rotated later in the dashboard.',
  }),
});

export const CreateWebhookResponseValidationSchema = z.object({
  data: WebhookWithSecretResponseValidationSchema,
});

export const GetWebhooksResponseValidationSchema = PaginatedListSchema(
  WebhookResponseValidationSchema,
);

export const DeleteWebhookResponseValidationSchema = z.object({
  data: WebhookResponseValidationSchema,
});

export const GetWebhookResponseValidationSchema = z.object({
  data: WebhookResponseValidationSchema,
});

export const UpdateWebhookResponseValidationSchema = z.object({
  data: WebhookResponseValidationSchema,
});

const WebhookToolDataValidationSchema = WebhookResponseValidationSchema.extend({
  dashboardUrl: DashboardUrlField,
});

export const CreateWebhookToolResponseValidationSchema = z.object({
  data: WebhookToolDataValidationSchema,
});

export const UpdateWebhookToolResponseValidationSchema = z.object({
  data: WebhookToolDataValidationSchema,
});

export const GetWebhookToolResponseValidationSchema = z.object({
  data: WebhookToolDataValidationSchema,
});

export const GetWebhooksToolResponseValidationSchema = PaginatedListSchema(
  WebhookToolDataValidationSchema,
);
